import { Injectable } from '@angular/core';
import {
  NotaView,
  ItemEducacional,
  Periodo,
  Encerramento,
  EnturmacaoCompleta,
  CalculoFormula,
  LinhaDesempenho,
  ColunaDesempenho
} from '../models/nota-types';
import { AvaliacaoService, RegistroAvaliacao, ItemAvaliavel } from '../../../core/config/services/avaliacao.service';
import { NotaValidationService } from './nota-validation.service';
import { NotaTransformerService } from './nota-transformer.service';
import { PeriodoProcessorService } from './periodo-processor.service';

/**
 * Serviço responsável por construir views de desempenho escolar
 * Separação de responsabilidades do NotaService
 */
@Injectable({
  providedIn: 'root'
})
export class NotaViewBuilderService {
  constructor(
    private notaValidation: NotaValidationService,
    private notaTransformer: NotaTransformerService,
    private periodoProcessor: PeriodoProcessorService
  ) {}

  /** Estrutura baseada no DesempenhoEscolarViewService do AngularJS. */
  createView(
    registros: { avaliacoes: { itensAvaliaveis: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[]; calculo?: CalculoFormula }; encerramentos?: Encerramento[] },
    itemEducacional: ItemEducacional,
    periodo: Periodo | null,
    enturmacoes: EnturmacaoCompleta[],
    encerramentosTurma: Encerramento[],
    porInstrumento = true
  ): { colunas: ColunaDesempenho[]; linhas: LinhaDesempenho[]; colunaMediaPeriodo?: ColunaDesempenho | null; calculoFormula?: CalculoFormula | null } {
    const avaliacoes = this.extractAvaliacoes(registros.avaliacoes);
    const itensAvaliaveis = registros.avaliacoes.itensAvaliaveis || [];
    const calculoFormula = registros.avaliacoes.calculo || null;

    const colunas = this.createColunas(itensAvaliaveis, porInstrumento);
    const colunaMediaPeriodo = this.createColunaMediaPeriodo(itensAvaliaveis, periodo, encerramentosTurma);

    if (!enturmacoes || enturmacoes.length === 0) {
      return { colunas, linhas: [], colunaMediaPeriodo, calculoFormula };
    }

    const linhas = this.createLinhas(enturmacoes, colunas, colunaMediaPeriodo, avaliacoes, itensAvaliaveis, itemEducacional);

    return { colunas, linhas, colunaMediaPeriodo, calculoFormula };
  }

  createYearView(
    registros: { avaliacoes: { itensAvaliaveis: ItemAvaliavel[]; registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[]; calculo?: CalculoFormula }; abonos?: unknown[] },
    itemEducacional: ItemEducacional,
    itensAvaliaveis: ItemAvaliavel[],
    periodos: Periodo[],
    enturmacoes: RegistroAvaliacao['enturmacao'][]
  ): { colunas: ColunaDesempenho[]; linhas: LinhaDesempenho[]; colunaMediaFinal?: ColunaDesempenho | null } {
    const avaliacoes = this.extractAvaliacoes(registros.avaliacoes);
    const avaliacoesFiltradas = this.filterAvaliacoesForYearView(avaliacoes, itemEducacional);
    const itensAvaliaveisFiltrados = this.filterItensAvaliaveisForYearView(itensAvaliaveis, itemEducacional);

    const periodosProcessados = this.periodoProcessor.processPeriodosForYearView(
      itensAvaliaveisFiltrados,
      periodos
    );

    const periodosOrdenados = this.periodoProcessor.sortAndFilterPeriodos(periodosProcessados, itensAvaliaveisFiltrados, itemEducacional);
    const colunas = this.createColunasFromPeriodos(periodosOrdenados);
    const colunaMediaFinal = this.createColunaMediaFinal(itensAvaliaveis);
    const linhas = this.createLinhasForYearView(
      enturmacoes,
      colunas,
      colunaMediaFinal,
      avaliacoesFiltradas,
      itensAvaliaveisFiltrados,
      itemEducacional
    );

    return { colunas, linhas, colunaMediaFinal };
  }

  createNotaView(registro: RegistroAvaliacao): NotaView {
    const notaView: NotaView = {
      id: registro.id,
      notaNumerica: registro.notaNumerica,
      notaDescritiva: registro.notaDescritiva,
      notaConceito: registro.notaConceito,
      avaliavel: registro.avaliavel,
      enturmacao: registro.enturmacao,
      $$nota: this.notaTransformer.getNotaValue(registro),
      $$editavel: this.notaValidation.isNotaEditavel(registro),
      $$editavelAnual: this.notaValidation.isNotaEditavelAnual(registro),
      $$semNota: this.notaValidation.isSemNota(registro),
      $$permiteParecer: this.notaValidation.permiteParecer(registro),
      $$permiteParecerAnual: this.notaValidation.permiteParecerAnual(registro),
      $$permiteCha: this.notaValidation.permiteCha(registro),
      hasNotaFilhosPreenchido: registro.hasNotaFilhosPreenchido || false,
      $$nocache: Date.now().toString() + Math.random().toString(),
      updateFields: () => this.updateNotaFields(notaView)
    };

    // Preserva parecer vindo do backend p/ o indicador visual sobreviver a um reload da página.
    const notaViewCompat = notaView as NotaView & { parecer?: string; parecerDetalhadoConteudo?: string };
    if (registro.parecer) {
      notaViewCompat.parecer = registro.parecer;
    }
    if (registro.parecerDetalhadoConteudo) {
      notaViewCompat.parecerDetalhadoConteudo = registro.parecerDetalhadoConteudo;
    }

    return notaView;
  }

  createNotaViewVazia(avaliavel: ItemAvaliavel, enturmacao: EnturmacaoCompleta | { id: number }): NotaView {
    const enturmacaoCompleta = 'matricula' in enturmacao ? enturmacao as EnturmacaoCompleta : {
      id: enturmacao.id,
      matricula: { id: enturmacao.id, nome: '', situacao: null },
      numeroChamada: 0,
      situacao: null
    } as EnturmacaoCompleta;
    
    const registroVazio: { avaliavel: ItemAvaliavel; enturmacao: EnturmacaoCompleta; notaNumerica?: number; notaDescritiva?: string; notaConceito?: string } = {
      avaliavel,
      enturmacao: enturmacaoCompleta,
      notaNumerica: undefined,
      notaDescritiva: undefined,
      notaConceito: undefined
    };
    
    const notaView: NotaView = {
      avaliavel,
      enturmacao: enturmacaoCompleta,
      $$nota: null,
      $$editavel: this.notaValidation.isNotaEditavel(registroVazio),
      $$editavelAnual: this.notaValidation.isNotaEditavelAnual(registroVazio),
      $$semNota: this.notaValidation.isSemNota(registroVazio),
      $$permiteParecer: this.notaValidation.permiteParecer(registroVazio),
      $$permiteParecerAnual: this.notaValidation.permiteParecerAnual(registroVazio),
      $$permiteCha: this.notaValidation.permiteCha(registroVazio),
      hasNotaFilhosPreenchido: false,
      $$nocache: Date.now().toString() + Math.random().toString(),
      updateFields: () => this.updateNotaFields(notaView)
    };

    return notaView;
  }

  private extractAvaliacoes(avaliacoes: { registrosAvaliacao?: RegistroAvaliacao[]; registros?: RegistroAvaliacao[] }): RegistroAvaliacao[] {
    return avaliacoes.registrosAvaliacao || avaliacoes.registros || [];
  }

  private createColunas(itensAvaliaveis: ItemAvaliavel[], porInstrumento: boolean): ColunaDesempenho[] {
    const colunas: ColunaDesempenho[] = [];
    const colunasFiltradas = this.notaTransformer.filtrarItensAvaliaveisParaColunas(itensAvaliaveis, porInstrumento);
    
    colunasFiltradas.forEach(item => {
      const descricao = this.getDescricaoColuna(item);
      colunas.push({
        descricao,
        sigla: item.sigla,
        tipoPeriodo: item.tipoPeriodo,
        rawValue: item,
        data: item.data
      });
    });

    return colunas;
  }

  private getDescricaoColuna(item: ItemAvaliavel): string {
    if (item.tipoPeriodo === 'CALCULO_INSTRUMENTOS') {
      return 'Cálculo';
    }
    return item.descricao || '';
  }

  private createColunaMediaPeriodo(
    itensAvaliaveis: ItemAvaliavel[],
    periodo: Periodo | null,
    encerramentosTurma: Encerramento[]
  ): ColunaDesempenho | null {
    const itemMediaPeriodo = itensAvaliaveis.find(item => item.tipoPeriodo === 'MEDIA_PERIODO');
    if (!itemMediaPeriodo) {
      return null;
    }

    return {
      descricao: 'Média',
      sigla: itemMediaPeriodo.sigla,
      tipoPeriodo: 'MEDIA_PERIODO',
      rawValue: itemMediaPeriodo,
      data: itemMediaPeriodo.data
    };
  }

  private createLinhas(
    enturmacoes: EnturmacaoCompleta[],
    colunas: ColunaDesempenho[],
    colunaMediaPeriodo: ColunaDesempenho | null,
    avaliacoes: RegistroAvaliacao[],
    itensAvaliaveis: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): LinhaDesempenho[] {
    const linhas: LinhaDesempenho[] = [];

    enturmacoes.forEach(enturmacao => {
      const enturmacaoNormalizada = this.notaTransformer.normalizarEnturmacao(enturmacao);
      const linha: LinhaDesempenho = {
        enturmacao: enturmacaoNormalizada,
        notas: []
      };

      colunas.forEach((coluna, index) => {
        linha.notas[index] = this.createNotaForLinha(
          enturmacaoNormalizada,
          coluna,
          avaliacoes,
          itensAvaliaveis,
          itemEducacional
        );
      });

      if (colunaMediaPeriodo) {
        linha.notaMediaPeriodo = this.createNotaMediaPeriodo(
          enturmacaoNormalizada,
          colunaMediaPeriodo,
          avaliacoes,
          itensAvaliaveis
        );
      }

      this.notaTransformer.definirSituacaoEnturmacao(linha, enturmacaoNormalizada, itemEducacional);
      linhas.push(linha);
    });

    return linhas;
  }

  private createNotaForLinha(
    enturmacao: EnturmacaoCompleta,
    coluna: ColunaDesempenho,
    avaliacoes: RegistroAvaliacao[],
    itensAvaliaveis: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): NotaView {
    const avaliacao = avaliacoes.find(av => 
      av.enturmacao?.id === enturmacao.id &&
      av.avaliavel?.id === coluna.rawValue?.id
    );

    if (avaliacao) {
      const notaView = this.createNotaView(avaliacao);
      // Substitui enturmacao pelo objeto completo (enturmacoes-historicas), igual ao WEB legado,
      // garantindo que matricula.avaliacaoDescritiva e demais campos estejam presentes.
      notaView.enturmacao = enturmacao as unknown as typeof notaView.enturmacao;
      return notaView;
    }

    const itemAvaliavel = coluna.rawValue || itensAvaliaveis[0] || {} as ItemAvaliavel;
    return this.createNotaViewVazia(itemAvaliavel, enturmacao);
  }

  private createNotaMediaPeriodo(
    enturmacao: EnturmacaoCompleta,
    colunaMediaPeriodo: ColunaDesempenho,
    avaliacoes: RegistroAvaliacao[],
    itensAvaliaveis: ItemAvaliavel[]
  ): NotaView {
    const itemMediaVazio = colunaMediaPeriodo.rawValue || itensAvaliaveis.find(item => item.tipoPeriodo === 'MEDIA_PERIODO') || {} as ItemAvaliavel;
    return this.createNotaViewVazia(itemMediaVazio, enturmacao);
  }

  private filterAvaliacoesForYearView(avaliacoes: RegistroAvaliacao[], itemEducacional: ItemEducacional): RegistroAvaliacao[] {
    return avaliacoes.filter((avaliacao: RegistroAvaliacao) => {
      return avaliacao.avaliavel?.itemEducacional?.id === itemEducacional.id &&
             avaliacao.avaliavel?.tipoPeriodo !== 'INSTRUMENTO' &&
             avaliacao.avaliavel?.tipoPeriodo !== 'CALCULO_INSTRUMENTOS';
    });
  }

  private filterItensAvaliaveisForYearView(itensAvaliaveis: ItemAvaliavel[], itemEducacional: ItemEducacional): ItemAvaliavel[] {
    return itensAvaliaveis.filter(item => 
      item.itemEducacional?.id === itemEducacional.id &&
      item.tipoPeriodo !== 'CALCULO_INSTRUMENTOS'
    );
  }

  private createColunasFromPeriodos(periodosOrdenados: Array<{ descricao?: string; tipoPeriodo?: string; data?: string; dataInicial?: string; [key: string]: unknown }>): ColunaDesempenho[] {
    return periodosOrdenados.map(periodo => ({
      descricao: periodo.descricao || 'Período',
      sigla: periodo.descricao?.substring(0, 10) || '',
      tipoPeriodo: periodo.tipoPeriodo || 'PERIODO_AVALIATIVO',
      rawValue: periodo as unknown as ItemAvaliavel,
      data: periodo.data || periodo.dataInicial
    }));
  }

  private createColunaMediaFinal(itensAvaliaveis: ItemAvaliavel[]): ColunaDesempenho | null {
    const itemMediaFinal = itensAvaliaveis.find(item => item.tipoPeriodo === 'MEDIA_FINAL');
    if (!itemMediaFinal) {
      return null;
    }

    return {
      descricao: 'Média Final',
      sigla: 'MÉDIA FINAL',
      tipoPeriodo: 'MEDIA_FINAL',
      rawValue: itemMediaFinal,
      data: itemMediaFinal.data
    };
  }

  private createLinhasForYearView(
    enturmacoes: RegistroAvaliacao['enturmacao'][],
    colunas: ColunaDesempenho[],
    colunaMediaFinal: ColunaDesempenho | null,
    avaliacoesFiltradas: RegistroAvaliacao[],
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): LinhaDesempenho[] {
    const mapAvaliacoes = this.createMapAvaliacoes(avaliacoesFiltradas);
    const linhas: LinhaDesempenho[] = [];

    enturmacoes.forEach((enturmacao: EnturmacaoCompleta) => {
      const enturmacaoComFoto = this.preservarFotoEnturmacao(enturmacao);
      const linha = this.createLinhaForYearView(
        enturmacaoComFoto,
        colunas,
        colunaMediaFinal,
        mapAvaliacoes,
        itensAvaliaveisFiltrados,
        itemEducacional
      );
      linhas.push(linha);
    });

    return linhas;
  }

  private createMapAvaliacoes(avaliacoesFiltradas: RegistroAvaliacao[]): Map<string, RegistroAvaliacao> {
    const map = new Map<string, RegistroAvaliacao>();
    avaliacoesFiltradas.forEach((avaliacao: RegistroAvaliacao) => {
      const key = this.generateAvaliacaoKey(avaliacao);
      map.set(key, avaliacao);
    });
    return map;
  }

  private generateAvaliacaoKey(avaliacao: RegistroAvaliacao): string {
    const enturmacaoId = String(avaliacao.enturmacao?.id || '');
    const periodoIdentifier = this.extractPeriodoIdentifier(avaliacao);
    return enturmacaoId + periodoIdentifier;
  }

  private extractPeriodoIdentifier(avaliacao: RegistroAvaliacao): string {
    const avaliavel = avaliacao.avaliavel as ItemAvaliavel & { 
      periodoAvaliativo?: { id: number };
      idPeriodo?: number;
      identificadorExameFinal?: string;
    };
    
    if (avaliavel?.periodoAvaliativo?.id) {
      return avaliavel.periodoAvaliativo.id.toString();
    }
    if (avaliavel?.idPeriodo) {
      return avaliavel.idPeriodo.toString();
    }
    if (avaliacao.avaliavel?.tipoPeriodo === 'EXAME_FINAL' && avaliavel?.id) {
      const identificador = avaliavel.identificadorExameFinal || String(avaliavel.id);
      return `EXAME_FINAL_${identificador}`;
    }
    return avaliacao.avaliavel?.tipoPeriodo || '';
  }

  private preservarFotoEnturmacao(enturmacao: EnturmacaoCompleta): EnturmacaoCompleta {
    if (enturmacao.matricula && !enturmacao.matricula.foto && enturmacao.foto) {
      return {
        ...enturmacao,
        matricula: {
          ...enturmacao.matricula,
          foto: enturmacao.foto
        }
      };
    }
    return enturmacao;
  }

  private createLinhaForYearView(
    enturmacao: EnturmacaoCompleta,
    colunas: ColunaDesempenho[],
    colunaMediaFinal: ColunaDesempenho | null,
    mapAvaliacoes: Map<string, RegistroAvaliacao>,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): LinhaDesempenho {
    const linha: LinhaDesempenho = {
      enturmacao: this.definirSituacaoEnturmacaoYearView(enturmacao, itemEducacional),
      notas: []
    };

    colunas.forEach((coluna, index) => {
      linha.notas[index] = this.createNotaForColuna(
        enturmacao,
        coluna,
        mapAvaliacoes,
        itensAvaliaveisFiltrados,
        itemEducacional
      );
    });

    if (colunaMediaFinal) {
      linha.notaMediaFinal = this.createNotaMediaFinal(
        enturmacao,
        colunaMediaFinal,
        mapAvaliacoes
      );
    }

    return linha;
  }

  private definirSituacaoEnturmacaoYearView(enturmacao: EnturmacaoCompleta, itemEducacional: ItemEducacional): EnturmacaoCompleta {
    if (enturmacao.situacaoItensEducacionais && itemEducacional) {
      const situacaoItem = enturmacao.situacaoItensEducacionais?.find((sit: { itemEducacional?: { id: number }; situacao?: string }) =>
        sit.itemEducacional?.id === itemEducacional.id
      );
      if (situacaoItem) {
        return {
          ...enturmacao,
          situacao: situacaoItem.situacao || enturmacao.matricula?.situacao
        };
      }
    }
    return enturmacao;
  }

  private createNotaForColuna(
    enturmacao: EnturmacaoCompleta,
    coluna: ColunaDesempenho,
    mapAvaliacoes: Map<string, RegistroAvaliacao>,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): NotaView {
    const itemAvaliavelPeriodo = this.findItemAvaliavelForColuna(
      coluna,
      itensAvaliaveisFiltrados,
      itemEducacional
    );

    const periodoId = coluna.rawValue?.id?.toString() || coluna.tipoPeriodo;
    const key = String(enturmacao.id) + periodoId;
    const avaliacao = mapAvaliacoes.get(key);

    if (avaliacao) {
      const notaView = this.createNotaView(avaliacao);
      notaView.enturmacao = enturmacao as unknown as typeof notaView.enturmacao;
      return notaView;
    }

    const itemAvaliavel: ItemAvaliavel = itemAvaliavelPeriodo ?? this.createItemAvaliavelFallback(
      coluna,
      itemEducacional
    );
    return this.createNotaViewVazia(itemAvaliavel, enturmacao);
  }

  private createItemAvaliavelFallback(
    coluna: ColunaDesempenho,
    itemEducacional: ItemEducacional
  ): ItemAvaliavel {
    const rawValue = coluna.rawValue as ItemAvaliavel & { periodoAvaliativo?: { id: number } } | undefined;
    return {
      id: 0,
      descricao: coluna.descricao,
      tipoPeriodo: coluna.tipoPeriodo,
      modoAvaliacao: 'NUMERICO',
      periodoAvaliativo: rawValue?.periodoAvaliativo,
      itemEducacional: { id: itemEducacional.id, descricao: itemEducacional.descricao }
    };
  }

  private findItemAvaliavelForColuna(
    coluna: ColunaDesempenho,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): ItemAvaliavel | undefined {
    const periodoId = this.extractPeriodoIdFromColuna(coluna);

    if (this.isPeriodoAvaliativo(coluna.tipoPeriodo)) {
      return this.findItemByPeriodoAvaliativo(
        periodoId,
        itensAvaliaveisFiltrados,
        itemEducacional
      );
    }

    return this.findItemByTipoPeriodo(
      periodoId,
      coluna.tipoPeriodo,
      itensAvaliaveisFiltrados,
      itemEducacional
    );
  }

  private extractPeriodoIdFromColuna(coluna: ColunaDesempenho): string | undefined {
    const rawValue = coluna.rawValue as { id?: number | string } | undefined;
    return rawValue?.id?.toString();
  }

  private isPeriodoAvaliativo(tipoPeriodo?: string): boolean {
    return tipoPeriodo === 'PERIODO_AVALIATIVO' || !tipoPeriodo;
  }

  private findItemByPeriodoAvaliativo(
    periodoId: string | undefined,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): ItemAvaliavel | undefined {
    return itensAvaliaveisFiltrados.find(item => {
      const itemWithPeriodo = item as ItemAvaliavel & { periodoAvaliativo?: { id: number } };
      return itemWithPeriodo.periodoAvaliativo?.id?.toString() === periodoId &&
             item.itemEducacional?.id === itemEducacional.id;
    });
  }

  private findItemByTipoPeriodo(
    periodoId: string | undefined,
    tipoPeriodo: string | undefined,
    itensAvaliaveisFiltrados: ItemAvaliavel[],
    itemEducacional: ItemEducacional
  ): ItemAvaliavel | undefined {
    return itensAvaliaveisFiltrados.find(item => {
      const itemWithPeriodo = item as ItemAvaliavel & { idPeriodo?: number };
      return (itemWithPeriodo.idPeriodo?.toString() === periodoId ||
              item.tipoPeriodo === tipoPeriodo) &&
             item.itemEducacional?.id === itemEducacional.id;
    });
  }

  private createNotaMediaFinal(
    enturmacao: EnturmacaoCompleta,
    colunaMediaFinal: ColunaDesempenho,
    mapAvaliacoes: Map<string, RegistroAvaliacao>
  ): NotaView {
    const key = String(enturmacao.id) + 'MEDIA_FINAL';
    const avaliacao = mapAvaliacoes.get(key);

    if (avaliacao) {
      return this.createNotaView(avaliacao);
    }

    if (colunaMediaFinal.rawValue) {
      return this.createNotaViewVazia(colunaMediaFinal.rawValue, enturmacao);
    }

    return this.createNotaViewVazia({} as ItemAvaliavel, enturmacao);
  }

  private updateNotaFields(nota: NotaView): void {
    nota.$$nota = this.notaTransformer.getNotaValue(nota as RegistroAvaliacao);
  }
}

